import { Link } from 'react-router-dom';

interface FaqStep {
  title: string;
  description: React.ReactNode;
}

interface FaqQuestion {
  question: string;
  answer: React.ReactNode;
}

export const HOW_IT_WORKS_STEPS: FaqStep[] = [
  {
    title: 'Your balance grows',
    description: (
      <>
        You receive <span className="font-semibold">$625 USD every 3 months</span>. Your first
        top-up is added 3 weeks after your start date, then every 3 months after that.
      </>
    ),
  },
  {
    title: 'Check your next top-up + available amount',
    description: (
      <>
        The Unleash app is your central place to view your balance, next top-up date, and submit
        claims. Your balance rolls over, so you can save up for bigger purchases.
      </>
    ),
  },
  {
    title: 'Make a purchase (and keep the receipt)',
    description: (
      <>
        Use Unleash for anything that energises you, fuels a passion, or helps you grow. If
        something costs more than your balance, Unleash can cover part and you cover the rest.
      </>
    ),
  },
  {
    title: 'Submit your claim in the app',
    description: (
      <>
        Choose a{' '}
        <Link to="/categories" className="text-gray-500 hover:underline">
          category
        </Link>
        , enter the amount in USD, upload your receipt, and add a short description.
      </>
    ),
  },
];

export const FAQ_ITEMS: FaqQuestion[] = [
  {
    question: 'How much do I receive and when?',
    answer: (
      <>
        You receive <span className="font-semibold">$625 USD every 3 months</span>. Your first
        top-up is added 3 weeks after your start date, then every 3 months after that.
      </>
    ),
  },
  {
    question: 'Who is eligible for top-ups?',
    answer: (
      <>
        You&apos;re eligible while assigned to an{' '}
        <span className="font-semibold">active project</span> or the{' '}
        <span className="font-semibold">Core Team</span>. For Client-Based Engineers (CBEs), top-ups
        pause when work ends with a client.
      </>
    ),
  },
  {
    question: 'Do balances roll over?',
    answer: (
      <>
        Yes. Your balance rolls over{' '}
        <span className="font-semibold">quarter-to-quarter and year-to-year</span>. This means you
        can save up for larger purchases over time.
      </>
    ),
  },
  {
    question: 'Is there a maximum balance?',
    answer: (
      <>
        <p>
          Your balance is capped at <span className="font-semibold">$3,000 USD</span>. If
          you&apos;re within $625 of the cap, your top-up will only fill the gap — it won&apos;t
          exceed $3,000.
        </p>
        <p className="mt-2">
          For example, if your balance is $2,800, you&apos;d receive a $200 top-up. The remaining
          $425 is forfeited.
        </p>
      </>
    ),
  },
  {
    question: 'How does the $500 monthly claim limit work?',
    answer: (
      <>
        There&apos;s a single <span className="font-semibold">$500 USD monthly limit</span> across
        all claims and categories combined. This resets at the start of each month.
      </>
    ),
  },
  {
    question: 'Are any categories exempt from the monthly limit?',
    answer: (
      <>
        Yes. <span className="font-semibold">X-Team Events</span> and{' '}
        <span className="font-semibold">Learning</span> do not count toward the $500 monthly limit.
        You can claim as much as your balance allows for those categories.
      </>
    ),
  },
  {
    question: 'What if my purchase costs more than my balance or the monthly limit?',
    answer: (
      <>
        Unleash works as a partnership — it covers part and you cover the difference. For example,
        if you want a $700 standing desk and your monthly limit is $500, you&apos;d claim $500
        through Unleash and pay $200 yourself.
      </>
    ),
  },
  {
    question: 'Do I need receipts and what currency should I use?',
    answer: (
      <>
        Yes — one receipt per claim. The amount you enter must be{' '}
        <span className="font-semibold">converted to USD</span>. Upload the original receipt as-is.
      </>
    ),
  },
  {
    question: "What can't I claim?",
    answer: (
      <>
        <p className="mb-2">The following are not eligible for reimbursement:</p>
        <ul className="list-inside list-disc space-y-1">
          <li>Work equipment (e.g., laptops)</li>
          <li>Medical expenses (prescriptions, insurance, hospitalisation)</li>
          <li>
            Illegal or high-risk items (controlled substances, gambling, illegal services, weapons)
          </li>
        </ul>
      </>
    ),
  },
];
